import { defineStore } from 'pinia'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { LEVELS } from '@components/ColorMatch/config'

export const useColorMatchStore = defineStore('color-match', {
	state: () => ({
		uuid: null,
		loading: false,
		error: null,
		resultsEndpoint: null,
		progressEndpoint: null,
		levels: LEVELS,
		step: 0,
		levelId: null,
		levelName: null,
		sublevelId: null,
		sublevelName: null,
		coverageId: null,
		coverageName: null,
		powderTypeId: null,
		powderTypeName: null,
		ageRange: null,
		fullName: null,
		email: null,
		sendEmail: false,
		results: null,
	}),

	getters: {
		isActiveLevel: (state) => (level) => state?.levelId === level,
		sublevelImages: (state) => state?.levels[state?.levelName]?.images ?? [],
		isActiveSublevel: (state) => (sublevel) => state?.sublevelName === sublevel,
	},

	actions: {
		setUuid(uuid) {
			if (!this.uuid) {
				this.uuid = uuid
			}
		},
		setResultsEndpoint(resultsEndpoint) {
			this.resultsEndpoint = resultsEndpoint
		},
		setProgressEndpoint(progressEndpoint) {
			this.progressEndpoint = progressEndpoint
		},
		setEmailEndpoint(emailEndpoint) {
			this.emailEndpoint = emailEndpoint
		},
		setStep(step) {
			this.step = step
			if (step === 6) {
				// was 7 - remove info double input
				// prepare results
				this.getResults()
			}
			this.saveProgress()
		},
		setLevelId(levelId) {
			this.levelId = levelId
			// reset next applicalbe step(s)
			this.sublevelId = null
			this.sublevelName = null
			this.powderType = null
		},
		setLevelName(levelName) {
			this.levelName = levelName
			// reset next applicalbe step(s)
			this.sublevelId = null
			this.sublevelName = null
			this.powderType = null
		},
		setSublevelName(sublevelName) {
			this.sublevelName = sublevelName
		},
		setSublevelId(sublevelId) {
			this.sublevelId = sublevelId
		},
		setCoverage(coverage) {
			this.coverage = coverage
		},
		setCoverageName(coverageName) {
			this.coverageName = coverageName
		},
		setCoverageId(coverageId) {
			this.coverageId = coverageId
		},
		setPowderTypeName(powderTypeName) {
			this.powderTypeName = powderTypeName
		},
		setPowderTypeId(powderTypeId) {
			this.powderTypeId = powderTypeId
		},
		setPowderType(powderType) {
			this.powderType = powderType
		},
		setAgeRange(ageRange) {
			this.ageRange = ageRange
		},
		setFullName(fullName) {
			this.fullName = fullName
		},
		setEmail(email) {
			this.email = email
		},
		setSendEmail(sendEmail) {
			this.sendEmail = sendEmail
		},
		reset() {
			this.uuid = uuidv4()
			this.loading = false
			this.error = null
			this.step = 0
			this.levelId = null
			this.levelName = null
			this.sublevelId = null
			this.sublevelName = null
			this.coverageId = null
			this.coverageName = null
			this.powderTypeId = null
			this.powderTypeName = null
			this.ageRange = null
			this.fullName = null
			this.email = null
			this.sendEmail = false
			this.results = null
		},
		async saveProgress({ add_to_cart_sku = null } = {}) {
			try {
				const payload = {
					uuid: this.uuid,
					step_id: this.step,
					level_id: this.levelId,
					sublevel_id: this.sublevelId,
					coverage_id: this.coverageId,
					age_range: this.ageRange,
					full_name: this.fullName,
					email_address: this.email,
					add_to_cart_sku,
				}
				await axios.post(this.progressEndpoint, payload)
			} catch (_) {}
		},
		sendResults({
			fullName = null,
			email = null,
			sendEmail = false,
			shareWithAffiliate = false,
			affiliate = null,
		} = {}) {
			this.getResults({ fullName, email, sendEmail, shareWithAffiliate, affiliate })
		},
		async getResults({
			fullName = null,
			email = null,
			sendEmail = false,
			shareWithAffiliate = false,
			affiliate = null,
		} = {}) {
			try {
				this.loading = true
				const payload = {
					level_id: this.levelId,
					sublevel_id: this.sublevelId,
					finish_type_id: this.powderTypeId,
					coverage_id: this.coverageId,
					full_name: fullName ?? this.fullName,
					email_address: email ?? this.email,
					send_email: [sendEmail, this.sendEmail].some(Boolean),
					send_artist_email: false,
				}

				if (affiliate && shareWithAffiliate) {
					const { customerId: affiliateCustomerId, email: affiliateEmail } = affiliate
					payload.send_artist_email = true
					payload.artist_email_address = affiliateEmail
					payload.artist_customer_id = affiliateCustomerId
				}

				const { data } = await axios.post(this.resultsEndpoint, payload)
				this.results = data.results
				this.loading = false
			} catch (err) {
				const message = err?.response?.data?.message ?? 'Something went wrong..'
				this.error = message
				this.loading = false
			}
		},
	},

	persist: {
		enabled: true,
	},
})
